import { netsuiteIntegrationSelectors } from '@vy/app-selectors';
import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import { RadioGroup } from '../../../../components/RadioGroup';
import { ConfigurationSection } from '../../../../containers/ConfigurationSection';
import { getFlagFromStatus, getStatusByFlag } from '../../../../types/boolean-flag';
import type {
  NetSuiteConfigurationControl,
  NetSuiteConfigurationFormValues,
} from '../../NetSuiteConfigurationFormSchema';
import {
  AUTOMATICALLY_SYNC_INVOICES_OPTIONS,
  INVOICE_NUMBER_SYNC_STRATEGY_OPTIONS,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY_OPTIONS,
  LINK_INVOICE_TO_SALES_ORDER_OPTIONS,
  OVERRIDE_LINE_ITEM_QUANTITY_OPTIONS,
  SUMMARIZE_LINE_ITEMS_BY_PRODUCT_ID_OPTIONS,
} from './constants';

type InvoicesConfigurationSectionProps = {
  control: NetSuiteConfigurationControl;
  defaultValues: NetSuiteConfigurationFormValues;
};

export const InvoicesConfigurationSection: FC<InvoicesConfigurationSectionProps> = ({
  control,
  defaultValues,
}) => (
  <ConfigurationSection title="Invoices" iconName="InvoiceListItem">
    <Controller
      name="autoExportInvoices"
      control={control}
      defaultValue={defaultValues.autoExportInvoices}
      render={({ field: { value, onChange } }) => (
        <div data-vy={netsuiteIntegrationSelectors.configuration.autoExportInvoicesToggle}>
          <RadioGroup
            title="Choose how to sync approved invoices"
            value={getFlagFromStatus(value)}
            onChange={(selected) => onChange(getStatusByFlag(selected))}
            keyExtractor={(option) => option.value}
            options={AUTOMATICALLY_SYNC_INVOICES_OPTIONS}
          />
        </div>
      )}
    />
    <Controller
      name="invoiceNumberSyncStrategy"
      control={control}
      defaultValue={defaultValues.invoiceNumberSyncStrategy}
      render={({ field: { value, onChange } }) => (
        <div data-vy={netsuiteIntegrationSelectors.configuration.invoiceNumberStrategyRadio}>
          <RadioGroup
            title="Choose how invoice numbers are managed"
            value={value}
            onChange={onChange}
            keyExtractor={(option) => option.value}
            options={INVOICE_NUMBER_SYNC_STRATEGY_OPTIONS}
          />
        </div>
      )}
    />
    <Controller
      name="invoicePaymentStatusSyncStrategy"
      disabled
      control={control}
      defaultValue={defaultValues.invoicePaymentStatusSyncStrategy}
      render={({ field }) => (
        <div data-vy={netsuiteIntegrationSelectors.configuration.invoicePaymentStatusStrategyRadio}>
          <RadioGroup
            {...field}
            title="Choose how invoice payment status is managed"
            keyExtractor={(option) => option.value}
            options={INVOICE_PAYMENT_STATUS_SYNC_STRATEGY_OPTIONS}
          />
        </div>
      )}
    />
    <Controller
      name="summarizeLineItemsByProductId"
      control={control}
      defaultValue={defaultValues.summarizeLineItemsByProductId}
      render={({ field: { value, onChange } }) => (
        <div data-vy={netsuiteIntegrationSelectors.configuration.summarizeLineItemsToggle}>
          <RadioGroup
            title="Choose invoice posting format"
            value={getFlagFromStatus(value)}
            onChange={(selected) => onChange(getStatusByFlag(selected))}
            keyExtractor={(option) => option.value}
            options={SUMMARIZE_LINE_ITEMS_BY_PRODUCT_ID_OPTIONS}
          />
        </div>
      )}
    />
    <Controller
      name="overrideLineItemQuantity"
      control={control}
      defaultValue={defaultValues.overrideLineItemQuantity}
      render={({ field: { value, onChange } }) => (
        <div data-vy={netsuiteIntegrationSelectors.configuration.overrideQuantityToggle}>
          <RadioGroup
            title="Choose how line item quantity is submitted"
            value={getFlagFromStatus(value)}
            onChange={(selected) => onChange(getStatusByFlag(selected))}
            keyExtractor={(option) => option.value}
            options={OVERRIDE_LINE_ITEM_QUANTITY_OPTIONS}
          />
        </div>
      )}
    />
    <Controller
      name="linkInvoiceToSalesOrder"
      control={control}
      defaultValue={defaultValues.linkInvoiceToSalesOrder}
      render={({ field: { value, onChange } }) => (
        <RadioGroup
          title="Link invoice to its Sales Order in NetSuite"
          value={getFlagFromStatus(value)}
          onChange={(selected) => onChange(getStatusByFlag(selected))}
          keyExtractor={(option) => option.value}
          options={LINK_INVOICE_TO_SALES_ORDER_OPTIONS}
        />
      )}
    />
  </ConfigurationSection>
);
